import type { TFunction } from 'i18next';
import { camelCase, compact, groupBy, isEqual, omit, snakeCase, sortBy, uniqWith } from 'lodash';

import { getLocalizedDetail } from '../../../core/utils/localization.ts';
import { ValidationCheck } from '../../components/DocumentConfig.tsx';
import { ValidationCheckResultStatus } from '../../constants.ts';

export interface ValidationCheckResult {
    name: string;
    result: string;
    targetValue?: string | null;
    isBlocking?: boolean | null;
    explanationDetails?: Record<string, any> | null;
}

export interface ItemRef {
    itemType: string;
    itemIndex: number;
}

interface FieldValidationCheck {
    check: ValidationCheckResult;
    fieldName?: string;
}

interface LocatedValidationCheck extends FieldValidationCheck {
    item?: ItemRef;
}

export interface ValidationCheckSummary {
    message: string;
    items: ItemRef[];
    hasDocumentCheck: boolean;
}

// Document-level matching outcomes are explained per field by MatchingExplanation, so they are not listed again
const HIDDEN_VALIDATION_CHECK_NAMES: string[] = [ValidationCheck.OrderMatching, ValidationCheck.OfferMatching];

// validationChecks is read directly per node; the explanation keys hold free-form JSON that never contains checks
const SKIPPED_KEYS = new Set(['validationChecks', 'explanationDetails', 'confidenceExplanation']);
const MAX_DEPTH = 8;

const isFailedValidationCheck = (check: ValidationCheckResult): boolean =>
    check.result === ValidationCheckResultStatus.FAILED;

const isDisplayedValidationCheck = (check: ValidationCheckResult): boolean =>
    isFailedValidationCheck(check) && !HIDDEN_VALIDATION_CHECK_NAMES.includes(check.name);

// data_incomplete checks don't block the document
// For now only agentic rule errors are shown
const DISPLAYED_INCOMPLETE_CHECK_NAMES: string[] = [
    ValidationCheck.AgenticExtractionFailed,
    ValidationCheck.AgenticContextFilesTooLarge,
];

export const isDisplayedIncompleteCheck = (check: ValidationCheckResult): boolean =>
    check.result === ValidationCheckResultStatus.INCOMPLETE && DISPLAYED_INCOMPLETE_CHECK_NAMES.includes(check.name);

export const hasFailedValidationCheck = (validationChecks?: ValidationCheckResult[] | null): boolean =>
    (validationChecks ?? []).some(isFailedValidationCheck);

export const filterDisplayedValidationChecks = (
    validationChecks?: ValidationCheckResult[] | null
): ValidationCheckResult[] => uniqWith((validationChecks ?? []).filter(isDisplayedValidationCheck), isEqual);

const collectNodeChecks = (node: any, fieldName?: string, depth = 0): FieldValidationCheck[] => {
    if (!node || typeof node !== 'object' || depth > MAX_DEPTH) return [];
    if (Array.isArray(node)) return node.flatMap((item) => collectNodeChecks(item, fieldName, depth + 1));

    const ownChecks: ValidationCheckResult[] = Array.isArray(node.validationChecks) ? node.validationChecks : [];
    const nestedChecks = Object.entries(node).flatMap(([key, value]) =>
        SKIPPED_KEYS.has(key) ? [] : collectNodeChecks(value, snakeCase(key), depth + 1)
    );
    return [...ownChecks.map((check) => ({ check, fieldName })), ...nestedChecks];
};

export const collectValidationChecks = (document: any, itemTypes: string[]): LocatedValidationCheck[] => {
    if (!document) return [];

    const headerChecks = collectNodeChecks(omit(document, itemTypes.map(camelCase)));
    const itemChecks = itemTypes.flatMap((itemType) => {
        const items = document[camelCase(itemType)];
        if (!Array.isArray(items)) return [];
        return items.flatMap((item, itemIndex) =>
            collectNodeChecks(item).map((fieldCheck) => ({ ...fieldCheck, item: { itemType, itemIndex } }))
        );
    });

    return [...headerChecks, ...itemChecks];
};

// Customer-specific checks ship their message in the details instead of a translation key
const getBackendMessage = (check: ValidationCheckResult): string | undefined =>
    getLocalizedDetail(check.explanationDetails, 'text_val_check') ?? check.explanationDetails?.message;

export const getValidationCheckMessage = (t: TFunction, check: ValidationCheckResult, fieldName?: string): string => {
    // Crowdin exports untranslated keys as "" and i18next returns that as a hit, so empty strings fall through
    const translate = (key: string) => t(`validationChecks.${key}`, { ...check.explanationDetails, defaultValue: '' });
    // A check shared by several fields, like order_number_match on quote_number for offers, can word it per field
    const translated = (fieldName && translate(`${check.name}_${fieldName}`)) || translate(check.name);
    return translated || getBackendMessage(check) || t('validationChecks.unknownCheck', { name: check.name });
};

// Checks whose non-blocking failures only warn; every other field check keeps the error look it always had
const WARNING_WHEN_NON_BLOCKING: string[] = [ValidationCheck.ArticleStatus];

export const getFieldValidationCheckSeverity = (check: ValidationCheckResult): 'error' | 'warning' =>
    WARNING_WHEN_NON_BLOCKING.includes(check.name) && !check.isBlocking ? 'warning' : 'error';

export const getValidationCheckTargetValue = (t: TFunction, check: ValidationCheckResult): string | null =>
    check.targetValue ? `${t('validationChecks.targetValueLabel')}: ${check.targetValue}` : null;

export const summarizeValidationChecks = (
    t: TFunction,
    checks: LocatedValidationCheck[],
    isDisplayed: (check: ValidationCheckResult) => boolean = isDisplayedValidationCheck
): ValidationCheckSummary[] => {
    const rows = checks
        .filter(({ check }) => isDisplayed(check))
        .map(({ check, fieldName, item }) => ({
            message: compact([
                getValidationCheckMessage(t, check, fieldName),
                getValidationCheckTargetValue(t, check),
            ]).join(' '),
            item,
        }));

    const summaries = Object.values(groupBy(uniqWith(rows, isEqual), 'message')).map((group) => ({
        message: group[0].message,
        items: compact(group.map(({ item }) => item)),
        hasDocumentCheck: group.some(({ item }) => !item),
    }));

    return sortBy(summaries, [({ hasDocumentCheck }) => !hasDocumentCheck, ({ items }) => items.length > 0]);
};
